import React from 'react'
import {
    Box,
    Chip,
    IconButton,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import CalendarTodayIcon from '@material-ui/icons/CalendarToday'
import CreateIcon from '@material-ui/icons/Create'
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined'
import DirectionsWalkIcon from '@material-ui/icons/DirectionsWalk'
import EmailOutlinedIcon from '@material-ui/icons/EmailOutlined'
import EventAvailableIcon from '@material-ui/icons/EventAvailable'
import EventBusyIcon from '@material-ui/icons/EventBusy'
import LanguageIcon from '@material-ui/icons/Language'
import LinkIcon from '@material-ui/icons/Link'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline'
import PersonAddOutlinedIcon from '@material-ui/icons/PersonAddOutlined'
import PhoneIcon from '@material-ui/icons/Phone'
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf'
import SendIcon from '@material-ui/icons/Send'
import VerifiedUserOutlinedIcon from '@material-ui/icons/VerifiedUserOutlined'
import { SvgIconProps } from '@material-ui/core/SvgIcon'

import {
    PIPELINE_SOURCE_LABEL,
    PIPELINE_STAGE_LABEL,
    QUALIFYING_METHOD_LABEL,
    PipelineRecord,
    PipelineSource,
    PipelineStage,
    TourPipelineStatus,
} from '../types'
import {
    applicationFeeMark,
    getApplicationSubStatusDisplay,
} from '../../../../utils/applicationSubStatus'
import { PetHoverIcon } from '../../../../components/PetHoverIcon'
import {
    getBackgroundCheckDisplay,
    getScreeningRequestStatusDisplay,
    getScreeningResultDisplay,
} from '../../../../utils/screeningStatus'
import { AccountStatusChip } from '../InviteToApplyDialog'
import {
    resolveDemoStageLabel,
    resolveSubstageLabel,
} from '../pipelineSubstage'
import { NextActionTableIcon } from '../../components/NextActionMark'
import { formatRelativeAge } from '../relativeTime'

const useStyles = makeStyles((theme: Theme) => ({
    card: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
        overflow: 'hidden',
        flex: 1,
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
    },
    tableScroll: {
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
    },
    tableHead: {
        backgroundColor: theme.palette.grey[50],
        '& .MuiTableCell-head': {
            fontSize: '0.8125rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: theme.palette.text.secondary,
            whiteSpace: 'nowrap',
            position: 'sticky',
            top: 0,
            zIndex: 2,
            backgroundColor: theme.palette.grey[50],
        },
    },
    row: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    rowHighlighted: {
        backgroundColor: theme.palette.warning.light + '66',
    },
    name: {
        fontWeight: 600,
        fontSize: '1rem',
    },
    sub: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
    },
    empty: {
        padding: theme.spacing(5),
        textAlign: 'center',
        color: theme.palette.text.secondary,
        fontSize: '1rem',
    },
    barWrap: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minWidth: 100,
    },
    barTrack: {
        flex: 1,
        height: 6,
        borderRadius: 3,
        backgroundColor: theme.palette.grey[200],
        overflow: 'hidden',
    },
    barFill: {
        height: '100%',
        borderRadius: 3,
        backgroundColor: theme.palette.primary.main,
    },
    barValue: {
        fontSize: '0.8125rem',
        fontFamily: 'monospace',
        color: theme.palette.text.secondary,
        minWidth: 24,
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: theme.spacing(0.25),
        whiteSpace: 'nowrap',
    },
    iconButton: {
        padding: 6,
    },
    iconCol: {
        width: 1,
        whiteSpace: 'nowrap',
        paddingLeft: theme.spacing(1.5),
        paddingRight: theme.spacing(1.5),
    },
    opsCell: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'nowrap',
        gap: theme.spacing(0.5),
        whiteSpace: 'nowrap',
    },
    tourResolve: {
        display: 'inline-flex',
        alignItems: 'center',
        '& $iconButton': {
            padding: 4,
        },
    },
    statusRow: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 2,
    },
    microDate: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
    },
    lastActivity: {
        fontSize: '0.9375rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
        whiteSpace: 'nowrap',
    },
    interestPrimary: {
        fontSize: '0.875rem',
        color: theme.palette.text.primary,
        lineHeight: 1.3,
    },
    interestSecondary: {
        fontSize: '0.75rem',
        fontFamily: 'monospace',
        color: theme.palette.text.secondary,
        lineHeight: 1.2,
    },
    waitingText: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
        whiteSpace: 'nowrap',
    },
    nameRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        minWidth: 0,
    },
    nameIcons: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        flexShrink: 0,
        color: theme.palette.text.secondary,
    },
    metaIcon: {
        fontSize: '1.0625rem',
    },
    sourceCell: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
    },
    progressLine: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
    },
    progressLineStale: {
        fontSize: '0.75rem',
        color: theme.palette.warning.dark,
        fontWeight: 600,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
    },
}))

const SOURCE_ICONS: Record<
    PipelineSource,
    React.ComponentType<SvgIconProps>
> = {
    website: LanguageIcon,
    form: PersonAddOutlinedIcon,
    call: PhoneIcon,
    referral: PeopleOutlineIcon,
    walk_in: DirectionsWalkIcon,
    campaign: EmailOutlinedIcon,
}

function SourceIcon({
    source,
    className,
}: {
    source: PipelineSource
    className?: string
}) {
    const Icon = SOURCE_ICONS[source] ?? EmailOutlinedIcon
    const label = PIPELINE_SOURCE_LABEL[source]
    return (
        <Tooltip title={label}>
            <span className={className} style={{ display: 'inline-flex' }}>
                <Icon fontSize="inherit" style={{ fontSize: 'inherit' }} />
            </span>
        </Tooltip>
    )
}

function tourIconMeta(
    status?: TourPipelineStatus,
): {
    Icon: React.ComponentType<SvgIconProps>
    label: string
    color?: string
} | null {
    if (!status) return null
    switch (status) {
        case 'scheduled':
            return {
                Icon: CalendarTodayIcon,
                label: 'Tour scheduled',
            }
        case 'completed':
            return {
                Icon: EventAvailableIcon,
                label: 'Tour completed',
                color: '#047857',
            }
        case 'no_show':
            return {
                Icon: EventBusyIcon,
                label: 'Tour no show',
                color: '#b45309',
            }
        case 'skipped':
            return {
                Icon: CalendarTodayIcon,
                label: 'Tour skipped',
            }
        default:
            return null
    }
}

function TourMetaIcon({
    record,
    className,
}: {
    record: PipelineRecord
    className?: string
}) {
    const meta = tourIconMeta(record.tour_status)
    if (!meta) return null
    const { Icon, label, color } = meta
    const when = record.tour_date ? fmtDate(record.tour_date) : null
    const title = when ? `${label} · ${when}` : label
    return (
        <Tooltip title={title}>
            <span
                className={className}
                style={{ display: 'inline-flex', color }}
            >
                <Icon fontSize="inherit" style={{ fontSize: 'inherit' }} />
            </span>
        </Tooltip>
    )
}

function SourceLabelCell({ record }: { record: PipelineRecord }) {
    const classes = useStyles()
    return (
        <Box className={classes.sourceCell}>
            <Box className={classes.nameIcons}>
                <SourceIcon
                    source={record.source}
                    className={classes.metaIcon}
                />
            </Box>
            <Typography variant="caption" color="textSecondary">
                {PIPELINE_SOURCE_LABEL[record.source]}
            </Typography>
        </Box>
    )
}

function qualifyingMethodLabel(method?: string | null): string {
    if (!method) return '—'
    return QUALIFYING_METHOD_LABEL[method] ?? method
}

function fmtDate(iso?: string): string {
    if (!iso) return '—'
    try {
        return new Date(iso).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
        })
    } catch {
        return '—'
    }
}

function Bar({ value, warn }: { value: number; warn?: boolean }) {
    const classes = useStyles()
    const clamped = Math.min(100, Math.max(0, value))
    return (
        <Box className={classes.barWrap}>
            <Box className={classes.barTrack}>
                <Box
                    className={classes.barFill}
                    style={{
                        width: `${clamped}%`,
                        backgroundColor: warn ? '#E2563B' : undefined,
                    }}
                />
            </Box>
            <span className={classes.barValue}>{Math.round(clamped)}</span>
        </Box>
    )
}

function Pill({
    children,
    tone = 'neutral',
}: {
    children: React.ReactNode
    tone?: 'neutral' | 'good' | 'warn' | 'bad'
}) {
    const colors = {
        neutral: { bg: '#f3f4f6', fg: '#6b7280' },
        good: { bg: '#d1fae5', fg: '#047857' },
        warn: { bg: '#fef3c7', fg: '#b45309' },
        bad: { bg: '#ffe4e6', fg: '#be123c' },
    }[tone]
    return (
        <Chip
            label={children}
            size="small"
            style={{
                backgroundColor: colors.bg,
                color: colors.fg,
                fontWeight: 600,
                fontSize: '0.75rem',
                height: 24,
                textTransform: 'capitalize',
            }}
        />
    )
}

function LastActivityCell({ record }: { record: PipelineRecord }) {
    const classes = useStyles()
    const label = formatRelativeAge(record.last_activity_at)
    const absolute =
        record.last_activity_at != null
            ? new Date(record.last_activity_at).toLocaleString()
            : undefined
    return (
        <Typography className={classes.lastActivity} title={absolute}>
            {label}
        </Typography>
    )
}

function PipelineStageCell({
    record,
    tabLabelsById,
}: {
    record: PipelineRecord
    tabLabelsById?: Record<string, string>
}) {
    const label =
        (record.tabId && tabLabelsById?.[record.tabId]) ||
        record.tabLabel ||
        resolveDemoStageLabel(record) ||
        PIPELINE_STAGE_LABEL[record.stage]
    return <Pill tone="neutral">{label}</Pill>
}

function SubstageCell({ record }: { record: PipelineRecord }) {
    const classes = useStyles()
    const label = resolveSubstageLabel(record)
    return <Typography className={classes.interestPrimary}>{label}</Typography>
}

function NameCell({
    record,
    showMetaIcons = false,
}: {
    record: PipelineRecord
    /** Source + tour icons beside the name (later pipeline tabs). */
    showMetaIcons?: boolean
}) {
    const classes = useStyles()
    return (
        <Box>
            <Box className={classes.nameRow}>
                <NextActionTableIcon action={record.next_action} />
                {showMetaIcons && (
                    <Box className={classes.nameIcons}>
                        <SourceIcon
                            source={record.source}
                            className={classes.metaIcon}
                        />
                        <TourMetaIcon
                            record={record}
                            className={classes.metaIcon}
                        />
                    </Box>
                )}
                <Typography className={classes.name}>{record.name}</Typography>
                <PetHoverIcon pets={record.pets} className={classes.metaIcon} />
            </Box>
            <Typography className={classes.sub}>{record.email}</Typography>
        </Box>
    )
}

function resolveAccountDisplayStatus(
    record: PipelineRecord,
): 'none' | 'invited' | 'active' {
    const status = record.account_status ?? 'none'
    // Auto-invite: treat pending accounts as invited once an invite was sent.
    if (status === 'none' && record.invitation_sent_at) {
        return 'invited'
    }
    return status
}

function AccountStatusCell({ record }: { record: PipelineRecord }) {
    const classes = useStyles()
    const displayStatus = resolveAccountDisplayStatus(record)

    if (displayStatus === 'none') {
        return <Typography className={classes.waitingText}>—</Typography>
    }

    const statusAt =
        displayStatus === 'active'
            ? record.account_activated_at
            : record.invitation_sent_at
    const statusAge = formatRelativeAge(statusAt)

    return (
        <Box className={classes.statusRow}>
            <AccountStatusChip status={displayStatus} />
            {statusAge && statusAge !== '—' && (
                <Typography
                    className={classes.microDate}
                    title={
                        statusAt
                            ? new Date(statusAt).toLocaleString()
                            : undefined
                    }
                >
                    {statusAge}
                </Typography>
            )}
        </Box>
    )
}

function ActionIcon({
    title,
    disabled,
    onClick,
    href,
    children,
}: {
    title: string
    disabled?: boolean
    onClick?: (event: React.MouseEvent<HTMLElement>) => void
    href?: string
    children: React.ReactNode
}) {
    const classes = useStyles()
    const linkProps = href
        ? { href, target: '_blank' as const, rel: 'noopener noreferrer' }
        : {}
    return (
        <Tooltip title={title}>
            <span>
                <IconButton
                    size="small"
                    color="primary"
                    className={classes.iconButton}
                    disabled={disabled}
                    {...linkProps}
                    onClick={(event) => {
                        event.stopPropagation()
                        onClick?.(event)
                    }}
                >
                    {children}
                </IconButton>
            </span>
        </Tooltip>
    )
}

function AccountActionsCell({
    record,
    onInvite,
    inviting,
}: {
    record: PipelineRecord
    onInvite?: (leadId: number) => void
    inviting?: boolean
}) {
    const classes = useStyles()
    const displayStatus = resolveAccountDisplayStatus(record)
    const showResendInvite =
        record.isRealLead &&
        record.leadId != null &&
        displayStatus !== 'active' &&
        onInvite != null

    if (!showResendInvite) {
        return <Typography className={classes.waitingText}>—</Typography>
    }

    return (
        <Box className={classes.actions} onClick={(e) => e.stopPropagation()}>
            <ActionIcon
                title={inviting ? 'Sending…' : 'Resend invite'}
                disabled={inviting}
                onClick={() => onInvite(record.leadId!)}
            >
                <PersonAddOutlinedIcon fontSize="small" />
            </ActionIcon>
        </Box>
    )
}

function applicationActivityIso(record: PipelineRecord): string | null {
    const status = record.application_status
    return (
        record.workbook_last_activity_at ??
        record.application_fee_paid_at ??
        (status === 'sent'
            ? record.app_sent_date ?? record.last_activity_at
            : record.last_activity_at) ??
        null
    )
}

function isStaleActivity(iso: string | null, days = 7): boolean {
    if (iso == null) return false
    const ms = Date.parse(iso)
    if (Number.isNaN(ms)) return false
    return Date.now() - ms > days * 24 * 60 * 60 * 1000
}

function ApplicationStatusCell({ record }: { record: PipelineRecord }) {
    const classes = useStyles()
    const status = record.application_status
    const { label, tone } = getApplicationSubStatusDisplay(status)
    const feeMark = applicationFeeMark({
        feeCents: record.application_fee_cents,
        paidAt: record.application_fee_paid_at,
        paymentStatus: record.application_fee_status,
    })

    const sectionsComplete = record.workbook_sections_complete
    const sectionsTotal = record.workbook_sections_total
    const hasSectionProgress =
        sectionsComplete != null && sectionsTotal != null && sectionsTotal > 0

    const activityIso = applicationActivityIso(record)
    const activityLabel =
        activityIso != null ? formatRelativeAge(activityIso) : null
    const stale = isStaleActivity(activityIso)

    const progressParts: string[] = []
    if (hasSectionProgress) {
        progressParts.push(`${sectionsComplete}/${sectionsTotal}`)
    }
    if (activityLabel && activityLabel !== '—') {
        progressParts.push(activityLabel)
    }

    return (
        <Box className={classes.statusRow} onClick={(e) => e.stopPropagation()}>
            <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
                {status != null ? (
                    <Pill tone={tone}>{label}</Pill>
                ) : (
                    <Typography className={classes.microDate}>—</Typography>
                )}
                {feeMark === 'paid' && <Pill tone="good">Fee paid</Pill>}
                {feeMark === 'unpaid' && <Pill tone="warn">Fee unpaid</Pill>}
                {record.pending_application_review && (
                    <Pill tone="warn">Pending review</Pill>
                )}
            </Box>
            {progressParts.length > 0 && (
                <Typography
                    className={
                        stale ? classes.progressLineStale : classes.progressLine
                    }
                    title={
                        activityIso
                            ? new Date(activityIso).toLocaleString()
                            : undefined
                    }
                >
                    {progressParts.join(' · ')}
                    {stale ? ' · Stale' : ''}
                </Typography>
            )}
        </Box>
    )
}

function ApplicationActionsCell({
    record,
    onRemind,
    onSendScreeningLink,
    onDownloadPdf,
    reminding,
    sendingScreening,
    downloadingPdf,
}: {
    record: PipelineRecord
    onRemind?: (leadId: number) => void
    onSendScreeningLink?: (leadId: number) => void
    onDownloadPdf?: (leadId: number) => void
    reminding?: boolean
    sendingScreening?: boolean
    downloadingPdf?: boolean
}) {
    const classes = useStyles()
    const status = record.application_status

    const showRemind =
        record.isRealLead &&
        record.leadId != null &&
        onRemind != null &&
        status !== 'submitted' &&
        !record.pending_application_review

    const showSendScreening =
        record.isRealLead &&
        record.leadId != null &&
        onSendScreeningLink != null &&
        Boolean(record.can_send_screening_link)

    const showPdf =
        record.isRealLead &&
        record.leadId != null &&
        onDownloadPdf != null &&
        Boolean(record.has_submission_pdf)

    if (!showRemind && !showSendScreening && !showPdf) {
        return <Typography className={classes.waitingText}>—</Typography>
    }

    return (
        <Box className={classes.actions} onClick={(e) => e.stopPropagation()}>
            {showRemind && (
                <ActionIcon
                    title={reminding ? 'Sending…' : 'Remind'}
                    disabled={reminding}
                    onClick={() => onRemind(record.leadId!)}
                >
                    <EmailOutlinedIcon fontSize="small" />
                </ActionIcon>
            )}
            {showSendScreening && (
                <ActionIcon
                    title={sendingScreening ? 'Sending…' : 'Send screening'}
                    disabled={sendingScreening}
                    onClick={() => onSendScreeningLink(record.leadId!)}
                >
                    <VerifiedUserOutlinedIcon fontSize="small" />
                </ActionIcon>
            )}
            {showPdf && (
                <ActionIcon
                    title={downloadingPdf ? 'Preparing…' : 'Download PDF'}
                    disabled={downloadingPdf}
                    onClick={() => onDownloadPdf(record.leadId!)}
                >
                    <PictureAsPdfIcon fontSize="small" />
                </ActionIcon>
            )}
        </Box>
    )
}

function LeaseStatusPill({ record }: { record: PipelineRecord }) {
    if (
        record.lease_package_status === 'signed' ||
        record.stage === 'lease_signed'
    ) {
        return <Pill tone="good">Signed</Pill>
    }
    if (
        record.lease_package_status === 'awaiting_property' ||
        record.lease_awaiting_property
    ) {
        return <Pill tone="warn">Awaiting PM</Pill>
    }
    return <Pill tone="warn">Sent</Pill>
}

function LeasePackageActionCell({
    record,
    onSendLeasePackage,
    onSignAsPropertyManager,
    sendingLeasePackage,
    signingAsPropertyManager,
}: {
    record: PipelineRecord
    onSendLeasePackage?: (leadId: number) => void
    onSignAsPropertyManager?: (leadId: number) => void
    sendingLeasePackage?: boolean
    signingAsPropertyManager?: boolean
}) {
    const classes = useStyles()
    const leadId = record.leadId
    const show =
        record.isRealLead &&
        leadId != null &&
        (onSendLeasePackage != null || onSignAsPropertyManager != null)
    if (!show) {
        return (
            <Typography variant="caption" color="textSecondary">
                —
            </Typography>
        )
    }

    const signed =
        record.lease_package_status === 'signed' ||
        record.stage === 'lease_signed'
    const awaitingPm =
        record.lease_package_status === 'awaiting_property' ||
        record.lease_awaiting_property
    const alreadySent =
        Boolean(record.lease_sent_date) ||
        record.lease_package_status === 'sent'
    const showSign = awaitingPm && onSignAsPropertyManager != null
    const showSend =
        onSendLeasePackage != null && !signed && !awaitingPm && !alreadySent

    if (!showSign && !showSend) {
        return (
            <Typography variant="caption" color="textSecondary">
                —
            </Typography>
        )
    }

    return (
        <Box className={classes.actions} onClick={(e) => e.stopPropagation()}>
            {showSign && (
                <ActionIcon
                    title={signingAsPropertyManager ? 'Opening…' : 'Sign as PM'}
                    disabled={signingAsPropertyManager}
                    onClick={() => onSignAsPropertyManager(leadId!)}
                >
                    <CreateIcon fontSize="small" />
                </ActionIcon>
            )}
            {showSend && (
                <ActionIcon
                    title={
                        sendingLeasePackage ? 'Sending…' : 'Send lease package'
                    }
                    disabled={sendingLeasePackage}
                    onClick={() => onSendLeasePackage(leadId!)}
                >
                    <SendIcon fontSize="small" />
                </ActionIcon>
            )}
        </Box>
    )
}

function ScreeningActionCell({
    record,
    onStart,
    onResend,
    onSendLeaseOffer,
    starting,
    resending,
    sendingLeaseOffer,
}: {
    record: PipelineRecord
    onStart?: (leadId: number) => void
    onResend?: (leadId: number) => void
    onSendLeaseOffer?: (record: PipelineRecord) => void
    starting?: boolean
    resending?: boolean
    sendingLeaseOffer?: boolean
}) {
    const classes = useStyles()
    const showStart =
        record.isRealLead &&
        record.leadId != null &&
        record.can_start_screening &&
        onStart != null
    const showResend =
        record.isRealLead &&
        record.leadId != null &&
        record.can_resend_screening &&
        onResend != null
    const showSendLeaseOffer =
        record.isRealLead &&
        record.leadId != null &&
        record.can_send_lease_package &&
        record.user_id != null &&
        onSendLeaseOffer != null

    if (!showStart && !showResend && !showSendLeaseOffer) {
        return (
            <Typography variant="caption" color="textSecondary">
                —
            </Typography>
        )
    }

    return (
        <Box className={classes.actions} onClick={(e) => e.stopPropagation()}>
            {showStart && (
                <ActionIcon
                    title={starting ? 'Sending…' : 'Send screening invitation'}
                    disabled={starting}
                    onClick={() => onStart(record.leadId!)}
                >
                    <VerifiedUserOutlinedIcon fontSize="small" />
                </ActionIcon>
            )}
            {showResend && (
                <ActionIcon
                    title={
                        resending
                            ? '…'
                            : record.screening_status === 'INVITED'
                            ? 'Copy invite link'
                            : 'Resend screening'
                    }
                    disabled={resending}
                    onClick={() => onResend(record.leadId!)}
                >
                    <LinkIcon fontSize="small" />
                </ActionIcon>
            )}
            {showSendLeaseOffer && (
                <ActionIcon
                    title={sendingLeaseOffer ? 'Opening…' : 'Send lease offer'}
                    disabled={sendingLeaseOffer}
                    onClick={() => onSendLeaseOffer(record)}
                >
                    <DescriptionOutlinedIcon fontSize="small" />
                </ActionIcon>
            )}
        </Box>
    )
}

function TourStatusCell({
    record,
    onSchedule,
    onComplete,
    onNoShow,
    updating,
}: {
    record: PipelineRecord
    onSchedule?: (leadId: number) => void
    onComplete?: (leadId: number, tourId: number) => void
    onNoShow?: (leadId: number, tourId: number) => void
    updating?: boolean
}) {
    const classes = useStyles()
    const status = record.tour_status
    const canAct =
        record.isRealLead && record.leadId != null && onSchedule != null
    const canResolve =
        status === 'scheduled' &&
        record.tour_id != null &&
        record.leadId != null &&
        (onComplete != null || onNoShow != null)
    const statusMeta = tourIconMeta(status)
    const StatusIcon = statusMeta?.Icon
    const statusWhen = record.tour_date ? fmtDate(record.tour_date) : null
    const statusTitle = statusMeta
        ? statusWhen
            ? `${statusMeta.label} · ${statusWhen}`
            : statusMeta.label
        : 'Tour scheduled'

    return (
        <Box className={classes.opsCell} onClick={(e) => e.stopPropagation()}>
            {StatusIcon != null && (
                <ActionIcon title={statusTitle}>
                    <StatusIcon fontSize="small" />
                </ActionIcon>
            )}
            {canAct && status == null && (
                <ActionIcon
                    title="Schedule tour"
                    onClick={() => onSchedule(record.leadId!)}
                >
                    <CalendarTodayIcon fontSize="small" />
                </ActionIcon>
            )}
            {status != null && record.tour_date && (
                <Typography className={classes.microDate}>
                    {fmtDate(record.tour_date)}
                </Typography>
            )}
            {canResolve && (
                <Box className={classes.tourResolve}>
                    {onComplete != null && (
                        <ActionIcon
                            title={updating ? 'Updating…' : 'Mark complete'}
                            disabled={updating}
                            onClick={() =>
                                onComplete(record.leadId!, record.tour_id!)
                            }
                        >
                            <EventAvailableIcon fontSize="small" />
                        </ActionIcon>
                    )}
                    {onNoShow != null && (
                        <ActionIcon
                            title={updating ? 'Updating…' : 'Mark no-show'}
                            disabled={updating}
                            onClick={() =>
                                onNoShow(record.leadId!, record.tour_id!)
                            }
                        >
                            <EventBusyIcon fontSize="small" />
                        </ActionIcon>
                    )}
                </Box>
            )}
            {canAct &&
                (status === 'no_show' ||
                    status === 'completed' ||
                    status === 'skipped') && (
                    <ActionIcon
                        title="Schedule again"
                        onClick={() => onSchedule(record.leadId!)}
                    >
                        <CalendarTodayIcon fontSize="small" />
                    </ActionIcon>
                )}
        </Box>
    )
}

type Col = {
    key: string
    label: string
    render: (r: PipelineRecord) => React.ReactNode
    icon?: boolean
}

function LeadInterestCell({ record }: { record: PipelineRecord }) {
    const classes = useStyles()
    const unitLabel =
        record.unit_interest != null
            ? `${record.unit_interest}${
                  record.beds != null ? ` · ${record.beds}BR` : ''
              }`
            : null
    const budgetLabel = record.budget != null ? `$${record.budget}` : null

    if (unitLabel == null && budgetLabel == null) {
        return <Typography className={classes.microDate}>—</Typography>
    }

    return (
        <Box className={classes.statusRow}>
            <Typography className={classes.interestPrimary}>
                {unitLabel ?? '—'}
            </Typography>
            {budgetLabel != null && (
                <Typography className={classes.interestSecondary}>
                    {budgetLabel}
                </Typography>
            )}
        </Box>
    )
}

const STAGE_COLUMNS: Record<PipelineStage, Col[]> = {
    leads: [
        {
            key: 'interest',
            label: 'Interest',
            render: (r) => <LeadInterestCell record={r} />,
        },
    ],
    tours: [
        {
            key: 'status',
            label: 'Status',
            render: (r) => <TourStatusCell record={r} />,
        },
        {
            key: 'date',
            label: 'Tour date',
            render: (r) => (
                <Typography variant="caption">
                    {fmtDate(r.tour_date)}
                </Typography>
            ),
        },
        {
            key: 'staff',
            label: 'Staff',
            render: (r) => (
                <Typography variant="caption">{r.tour_staff ?? '—'}</Typography>
            ),
        },
        {
            key: 'unit',
            label: 'Unit interest',
            render: (r) => (
                <Typography variant="caption">
                    {r.unit_interest ?? '—'}
                    {r.beds != null ? ` · ${r.beds}BR` : ''}
                </Typography>
            ),
        },
    ],
    app_sent: [
        {
            key: 'qualifying_method',
            label: 'Qualifying method',
            render: (r) => (
                <Typography variant="caption">
                    {qualifyingMethodLabel(r.qualifying_method)}
                </Typography>
            ),
        },
        {
            key: 'interest',
            label: 'Interest',
            render: (r) => <LeadInterestCell record={r} />,
        },
    ],
    screening: [
        {
            key: 'status',
            label: 'Status',
            render: (r) => {
                const { label, tone } = getScreeningRequestStatusDisplay(
                    r.screening_status,
                )
                return <Pill tone={tone}>{label}</Pill>
            },
        },
        {
            key: 'result',
            label: 'Result',
            render: (r) => {
                const { label, tone } = getScreeningResultDisplay(
                    r.screening_result,
                )
                return <Pill tone={tone}>{label}</Pill>
            },
        },
        {
            key: 'bg',
            label: 'Background',
            render: (r) => {
                const { label, tone } = getBackgroundCheckDisplay(
                    r.background_check,
                )
                return <Pill tone={tone}>{label}</Pill>
            },
        },
        {
            key: 'credit',
            label: 'Credit',
            render: (r) => (
                <Typography
                    variant="caption"
                    style={{ fontFamily: 'monospace' }}
                >
                    {r.credit_score ?? '—'}
                </Typography>
            ),
        },
        {
            key: 'report',
            label: 'Report',
            icon: true,
            render: (r) =>
                r.screening_report_url ? (
                    <ActionIcon
                        title="Open report"
                        href={r.screening_report_url}
                    >
                        <OpenInNewIcon fontSize="small" />
                    </ActionIcon>
                ) : (
                    <Typography variant="caption" color="textSecondary">
                        —
                    </Typography>
                ),
        },
    ],
    leases: [
        {
            key: 'status',
            label: 'Status',
            render: (r) => <LeaseStatusPill record={r} />,
        },
        {
            key: 'sent',
            label: 'Sent',
            render: (r) => (
                <Typography variant="caption">
                    {fmtDate(r.lease_sent_date)}
                </Typography>
            ),
        },
        {
            key: 'progress',
            label: 'Signing',
            render: (r) =>
                r.lease_package_status === 'signed' ||
                r.stage === 'lease_signed' ? (
                    <Typography variant="caption">Complete</Typography>
                ) : (
                    <Bar value={r.lease_progress ?? 0} />
                ),
        },
        {
            key: 'signed',
            label: 'Signed',
            render: (r) => (
                <Typography variant="caption">
                    {fmtDate(r.lease_signed_date)}
                </Typography>
            ),
        },
        {
            key: 'missing',
            label: 'Missing',
            render: (r) => (
                <Typography variant="caption">
                    {r.lease_package_status === 'signed' ||
                    r.stage === 'lease_signed'
                        ? '—'
                        : (r.missing_signatures ?? []).join(', ') || '—'}
                </Typography>
            ),
        },
    ],
    lease_sent: [
        {
            key: 'status',
            label: 'Status',
            render: (r) => <LeaseStatusPill record={r} />,
        },
        {
            key: 'sent',
            label: 'Sent',
            render: (r) => (
                <Typography variant="caption">
                    {fmtDate(r.lease_sent_date)}
                </Typography>
            ),
        },
        {
            key: 'progress',
            label: 'Signing',
            render: (r) =>
                r.lease_package_status === 'signed' ? (
                    <Typography variant="caption">Complete</Typography>
                ) : (
                    <Bar value={r.lease_progress ?? 0} />
                ),
        },
        {
            key: 'missing',
            label: 'Missing',
            render: (r) => (
                <Typography variant="caption">
                    {(r.missing_signatures ?? []).join(', ') || '—'}
                </Typography>
            ),
        },
    ],
    lease_signed: [
        {
            key: 'status',
            label: 'Status',
            render: () => <Pill tone="good">Signed</Pill>,
        },
        {
            key: 'signed',
            label: 'Signed',
            render: (r) => (
                <Typography variant="caption">
                    {fmtDate(r.lease_signed_date)}
                </Typography>
            ),
        },
        {
            key: 'outstanding',
            label: 'Outstanding',
            render: (r) => (
                <Typography variant="caption">
                    {(r.outstanding_items ?? []).join(', ') || 'None'}
                </Typography>
            ),
        },
        {
            key: 'handoff',
            label: 'Handoff',
            render: (r) => (
                <Pill tone={r.handoff_status === 'complete' ? 'good' : 'warn'}>
                    {r.handoff_status ?? '—'}
                </Pill>
            ),
        },
    ],
    guarantor: [
        {
            key: 'status',
            label: 'Status',
            render: (r) => (
                <Pill
                    tone={
                        r.guarantor_status === 'approved'
                            ? 'good'
                            : r.guarantor_status === 'needed'
                            ? 'warn'
                            : 'neutral'
                    }
                >
                    {r.guarantor_status ?? '—'}
                </Pill>
            ),
        },
        {
            key: 'progress',
            label: 'Progress',
            render: (r) => <Bar value={r.guarantor_progress ?? 0} />,
        },
        {
            key: 'requested',
            label: 'Requested',
            render: (r) => (
                <Typography variant="caption">
                    {fmtDate(r.guarantor_requested)}
                </Typography>
            ),
        },
    ],
    future_resident: [
        {
            key: 'movein',
            label: 'Move-in',
            render: (r) => (
                <Typography variant="caption">
                    {fmtDate(r.move_in_date)}
                </Typography>
            ),
        },
        {
            key: 'checklist',
            label: 'Checklist',
            render: (r) => (
                <Bar
                    value={
                        ((r.checklist_done ?? 0) / (r.checklist_total || 1)) *
                        100
                    }
                />
            ),
        },
        {
            key: 'unit',
            label: 'Unit',
            render: (r) => (
                <Typography variant="caption">
                    {r.unit_interest ?? '—'}
                </Typography>
            ),
        },
    ],
}

const STAGE_ORDER: PipelineStage[] = [
    'leads',
    'tours',
    'app_sent',
    'screening',
    'leases',
    'lease_sent',
    'lease_signed',
    'guarantor',
    'future_resident',
]

function buildStageColumns(
    stage: PipelineStage,
    options?: {
        onRemindApplication?: (leadId: number) => void
        onInviteToApply?: (leadId: number) => void
        onSendScreeningLink?: (leadId: number) => void
        onDownloadApplicationPdf?: (leadId: number) => void
        onStartScreening?: (leadId: number) => void
        onResendScreening?: (leadId: number) => void
        onSendLeasePackage?: (leadId: number) => void
        onSendLeaseOffer?: (record: PipelineRecord) => void
        onSignAsPropertyManager?: (leadId: number) => void
        onScheduleTour?: (leadId: number) => void
        onCompleteTour?: (leadId: number, tourId: number) => void
        onNoShowTour?: (leadId: number, tourId: number) => void
        remindingLeadId?: number | null
        invitingLeadId?: number | null
        sendingScreeningLinkLeadId?: number | null
        downloadingPdfLeadId?: number | null
        startingScreeningLeadId?: number | null
        resendingScreeningLeadId?: number | null
        sendingLeasePackageLeadId?: number | null
        signingAsPropertyManagerLeadId?: number | null
        updatingTourLeadId?: number | null
        showStageColumn?: boolean
        tabLabelsById?: Record<string, string>
    },
): Col[] {
    const stageCol: Col = {
        key: 'pipeline_stage',
        label: 'Stage',
        render: (r) => (
            <PipelineStageCell
                record={r}
                tabLabelsById={options?.tabLabelsById}
            />
        ),
    }

    const base = STAGE_COLUMNS[stage]
    const withStage = options?.showStageColumn ? [stageCol, ...base] : base

    if (stage === 'screening') {
        return [
            ...withStage,
            {
                key: 'screening_actions',
                label: 'Actions',
                icon: true,
                render: (r) => (
                    <ScreeningActionCell
                        record={r}
                        onStart={options?.onStartScreening}
                        onResend={options?.onResendScreening}
                        onSendLeaseOffer={options?.onSendLeaseOffer}
                        starting={options?.startingScreeningLeadId === r.leadId}
                        resending={
                            options?.resendingScreeningLeadId === r.leadId
                        }
                    />
                ),
            },
        ]
    }

    if (stage === 'leases' || stage === 'lease_sent') {
        return [
            ...withStage,
            {
                key: 'lease_package_actions',
                label: 'Lease package',
                icon: true,
                render: (r) => (
                    <LeasePackageActionCell
                        record={r}
                        onSendLeasePackage={options?.onSendLeasePackage}
                        onSignAsPropertyManager={
                            options?.onSignAsPropertyManager
                        }
                        sendingLeasePackage={
                            options?.sendingLeasePackageLeadId === r.leadId
                        }
                        signingAsPropertyManager={
                            options?.signingAsPropertyManagerLeadId === r.leadId
                        }
                    />
                ),
            },
        ]
    }

    if (stage === 'app_sent') {
        return [
            ...withStage,
            {
                key: 'application_status',
                label: 'Application Status',
                render: (r) => <ApplicationStatusCell record={r} />,
            },
            {
                key: 'application_actions',
                label: 'Actions',
                icon: true,
                render: (r) => (
                    <ApplicationActionsCell
                        record={r}
                        onRemind={options?.onRemindApplication}
                        onSendScreeningLink={options?.onSendScreeningLink}
                        onDownloadPdf={options?.onDownloadApplicationPdf}
                        reminding={options?.remindingLeadId === r.leadId}
                        sendingScreening={
                            options?.sendingScreeningLinkLeadId === r.leadId
                        }
                        downloadingPdf={
                            options?.downloadingPdfLeadId === r.leadId
                        }
                    />
                ),
            },
        ]
    }

    if (stage === 'tours') {
        const withoutStatus = withStage.filter((c) => c.key !== 'status')
        return [
            {
                key: 'status',
                label: 'Status',
                render: (r) => (
                    <TourStatusCell
                        record={r}
                        onSchedule={options?.onScheduleTour}
                        onComplete={options?.onCompleteTour}
                        onNoShow={options?.onNoShowTour}
                        updating={options?.updatingTourLeadId === r.leadId}
                    />
                ),
            },
            ...withoutStatus,
        ]
    }

    if (stage !== 'leads') {
        return withStage
    }

    // Default leads stage columns when not using full roster layout.
    return [
        {
            key: 'interest',
            label: 'Interest',
            render: (r) => <LeadInterestCell record={r} />,
        },
        {
            key: 'tour',
            label: 'Tour',
            render: (r) => (
                <TourStatusCell
                    record={r}
                    onSchedule={options?.onScheduleTour}
                    onComplete={options?.onCompleteTour}
                    onNoShow={options?.onNoShowTour}
                    updating={options?.updatingTourLeadId === r.leadId}
                />
            ),
        },
        {
            key: 'account_status',
            label: 'Account status',
            render: (r) => <AccountStatusCell record={r} />,
        },
        {
            key: 'account_actions',
            label: 'Actions',
            icon: true,
            render: (r) => (
                <AccountActionsCell
                    record={r}
                    onInvite={options?.onInviteToApply}
                    inviting={options?.invitingLeadId === r.leadId}
                />
            ),
        },
    ]
}

function buildLeadsRosterColumns(options?: {
    onInviteToApply?: (leadId: number) => void
    onScheduleTour?: (leadId: number) => void
    onCompleteTour?: (leadId: number, tourId: number) => void
    onNoShowTour?: (leadId: number, tourId: number) => void
    invitingLeadId?: number | null
    updatingTourLeadId?: number | null
    tabLabelsById?: Record<string, string>
}): Col[] {
    return [
        {
            key: 'source',
            label: 'Source',
            render: (r) => <SourceLabelCell record={r} />,
        },
        {
            key: 'name',
            label: 'Name',
            render: (r) => <NameCell record={r} />,
        },
        {
            key: 'stage',
            label: 'Stage',
            render: (r) => (
                <PipelineStageCell
                    record={r}
                    tabLabelsById={options?.tabLabelsById}
                />
            ),
        },
        {
            key: 'substage',
            label: 'Substage',
            render: (r) => <SubstageCell record={r} />,
        },
        {
            key: 'last_activity',
            label: 'Last activity',
            render: (r) => <LastActivityCell record={r} />,
        },
        {
            key: 'interest',
            label: 'Interest',
            render: (r) => <LeadInterestCell record={r} />,
        },
        {
            key: 'tour',
            label: 'Tour',
            render: (r) => (
                <TourStatusCell
                    record={r}
                    onSchedule={options?.onScheduleTour}
                    onComplete={options?.onCompleteTour}
                    onNoShow={options?.onNoShowTour}
                    updating={options?.updatingTourLeadId === r.leadId}
                />
            ),
        },
        {
            key: 'account_status',
            label: 'Account status',
            render: (r) => <AccountStatusCell record={r} />,
        },
        {
            key: 'account_actions',
            label: 'Actions',
            icon: true,
            render: (r) => (
                <AccountActionsCell
                    record={r}
                    onInvite={options?.onInviteToApply}
                    inviting={options?.invitingLeadId === r.leadId}
                />
            ),
        },
    ]
}

interface StageTableProps {
    stage: PipelineStage
    rows: PipelineRecord[]
    isLoading?: boolean
    showStageColumn?: boolean
    /** Full column layout for the Leads tab roster (source → actions). */
    leadsRosterLayout?: boolean
    tabLabelsById?: Record<string, string>
    highlightedLeadId?: number | null
    emptyMessage?: string
    onRowClick: (record: PipelineRecord) => void
    onRemindApplication?: (leadId: number) => void
    onInviteToApply?: (leadId: number) => void
    onSendScreeningLink?: (leadId: number) => void
    onDownloadApplicationPdf?: (leadId: number) => void
    onStartScreening?: (leadId: number) => void
    onResendScreening?: (leadId: number) => void
    onSendLeasePackage?: (leadId: number) => void
    onSendLeaseOffer?: (record: PipelineRecord) => void
    onSignAsPropertyManager?: (leadId: number) => void
    onScheduleTour?: (leadId: number) => void
    onCompleteTour?: (leadId: number, tourId: number) => void
    onNoShowTour?: (leadId: number, tourId: number) => void
    remindingLeadId?: number | null
    invitingLeadId?: number | null
    sendingScreeningLinkLeadId?: number | null
    downloadingPdfLeadId?: number | null
    startingScreeningLeadId?: number | null
    resendingScreeningLeadId?: number | null
    sendingLeasePackageLeadId?: number | null
    signingAsPropertyManagerLeadId?: number | null
    updatingTourLeadId?: number | null
}

export const StageTable: React.FC<StageTableProps> = ({
    stage,
    rows,
    isLoading,
    showStageColumn = false,
    leadsRosterLayout = false,
    tabLabelsById,
    highlightedLeadId = null,
    emptyMessage = 'No people in this stage.',
    onRowClick,
    onRemindApplication,
    onInviteToApply,
    onSendScreeningLink,
    onDownloadApplicationPdf,
    onStartScreening,
    onResendScreening,
    onSendLeasePackage,
    onSendLeaseOffer,
    onSignAsPropertyManager,
    onScheduleTour,
    onCompleteTour,
    onNoShowTour,
    remindingLeadId,
    invitingLeadId,
    sendingScreeningLinkLeadId,
    downloadingPdfLeadId,
    startingScreeningLeadId,
    resendingScreeningLeadId,
    sendingLeasePackageLeadId,
    signingAsPropertyManagerLeadId,
    updatingTourLeadId,
}) => {
    const classes = useStyles()
    const cols = leadsRosterLayout
        ? buildLeadsRosterColumns({
              onInviteToApply,
              onScheduleTour,
              onCompleteTour,
              onNoShowTour,
              invitingLeadId,
              updatingTourLeadId,
              tabLabelsById,
          })
        : buildStageColumns(stage, {
              onRemindApplication,
              onInviteToApply,
              onSendScreeningLink,
              onDownloadApplicationPdf,
              onStartScreening,
              onResendScreening,
              onSendLeasePackage,
              onSendLeaseOffer,
              onSignAsPropertyManager,
              onScheduleTour,
              onCompleteTour,
              onNoShowTour,
              remindingLeadId,
              invitingLeadId,
              sendingScreeningLinkLeadId,
              downloadingPdfLeadId,
              startingScreeningLeadId,
              resendingScreeningLeadId,
              sendingLeasePackageLeadId,
              signingAsPropertyManagerLeadId,
              updatingTourLeadId,
              showStageColumn,
              tabLabelsById,
          })

    if (isLoading) {
        return (
            <Box className={classes.card} p={2}>
                <LinearProgress />
            </Box>
        )
    }

    if (rows.length === 0) {
        return (
            <Box className={classes.card}>
                <Typography className={classes.empty}>
                    {emptyMessage}
                </Typography>
            </Box>
        )
    }

    return (
        <Box className={classes.card}>
            <TableContainer className={classes.tableScroll}>
                <Table size="small">
                    <TableHead className={classes.tableHead}>
                        <TableRow>
                            {leadsRosterLayout ? (
                                cols.map((c) => (
                                    <TableCell
                                        key={c.key}
                                        className={
                                            c.icon ? classes.iconCol : undefined
                                        }
                                    >
                                        {c.label}
                                    </TableCell>
                                ))
                            ) : (
                                <>
                                    <TableCell>Name</TableCell>
                                    {stage !== 'app_sent' && (
                                        <TableCell>Last activity</TableCell>
                                    )}
                                    {cols.map((c) => (
                                        <TableCell
                                            key={c.key}
                                            className={
                                                c.icon
                                                    ? classes.iconCol
                                                    : undefined
                                            }
                                        >
                                            {c.label}
                                        </TableCell>
                                    ))}
                                </>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((r) => (
                            <TableRow
                                key={r.id}
                                hover
                                className={`${classes.row} ${
                                    r.leadId != null &&
                                    r.leadId === highlightedLeadId
                                        ? classes.rowHighlighted
                                        : ''
                                }`}
                                onClick={(e) => {
                                    const target = e.target as HTMLElement | null
                                    if (
                                        target?.closest?.(
                                            'button, a, input, textarea, select, label, [role="button"]',
                                        )
                                    ) {
                                        return
                                    }
                                    onRowClick(r)
                                }}
                            >
                                {leadsRosterLayout ? (
                                    cols.map((c) => (
                                        <TableCell
                                            key={c.key}
                                            className={
                                                c.icon
                                                    ? classes.iconCol
                                                    : undefined
                                            }
                                        >
                                            {c.render(r)}
                                        </TableCell>
                                    ))
                                ) : (
                                    <>
                                        <TableCell>
                                            <NameCell
                                                record={r}
                                                showMetaIcons
                                            />
                                        </TableCell>
                                        {stage !== 'app_sent' && (
                                            <TableCell>
                                                <LastActivityCell record={r} />
                                            </TableCell>
                                        )}
                                        {cols.map((c) => (
                                            <TableCell
                                                key={c.key}
                                                className={
                                                    c.icon
                                                        ? classes.iconCol
                                                        : undefined
                                                }
                                            >
                                                {c.render(r)}
                                            </TableCell>
                                        ))}
                                    </>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    )
}

function nextStage(stage: PipelineStage): PipelineStage | null {
    const idx = STAGE_ORDER.indexOf(stage)
    if (idx < 0 || idx >= STAGE_ORDER.length - 1) return null
    return STAGE_ORDER[idx + 1]
}

export { nextStage, STAGE_ORDER }
